import {inject, Injectable} from '@angular/core';
import {
  Downloader,
  EpgDownloadCenterService,
  epgDownloadProgressTrack,
} from '@epg-apps/epg-download';
import {
  HttpClient,
  HttpEventType,
  HttpHeaders,
  HttpResponse,
} from '@angular/common/http';
import {environment} from '@epg-apps/cdh-environments';
import {catchError, take, tap} from 'rxjs';
import {Router} from '@angular/router';
import {EpgDialogService} from '@epg-apps/epg-dialog';
import {CdhDownloadInitialDialogComponent} from '@epg-apps/cdh-download-initial-dialog';
import {CdhDownloadCompleteDialogComponent} from '@epg-apps/cdh-download-complete-dialog';

@Injectable({
  providedIn: 'root',
})
export class ExportService implements Downloader {
  private static readonly CUSTOMER_CDH_API = environment.cdhApi + 'spreadsheet';
  private readonly http = inject(HttpClient);
  private readonly downloadCenterService = inject(EpgDownloadCenterService);
  private readonly router = inject(Router);
  private readonly dialogService = inject(EpgDialogService);

  download(query = ''): void {
    const url = ExportService.CUSTOMER_CDH_API;
    let customerUUID = '';

    const refInitialDownloadDialog = this.dialogService.open(
      CdhDownloadInitialDialogComponent,
    );

    const CustomerEnvironmentsTabRegex =
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
    if (CustomerEnvironmentsTabRegex.test(this.router.url)) {
      customerUUID =
        RegExp(CustomerEnvironmentsTabRegex).exec(this.router.url)?.[0] ?? '';
    }

    if (!this.downloadCenterService.isDownloading()) {
      this.downloadCenterService.downloadState.update((state) => ({
        ...state,
        downloading: true,
        isCompleted: false,
      }));
      this.http
        .get(url, {
          headers: new HttpHeaders({
            'Accept':
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
          }),
          responseType: 'blob',
          observe: 'response',
          params: {
            query: query,
            customerUUID: customerUUID,
          },
        })
        .pipe(
          take(1),
          tap((response) =>
            epgDownloadProgressTrack(response, this.downloadCenterService),
          ),
          tap((response) => {
            if (response.type === HttpEventType.Response) {
              refInitialDownloadDialog.close();
              this.dialogService.open(CdhDownloadCompleteDialogComponent);
            }
          }),
          catchError((error) => {
            this.downloadCenterService.downloadState.update((state) => ({
              ...state,
              downloading: false,
              isCompleted: false,
            }));
            console.error(error);
            throw error;
          }),
        )
        .subscribe({
          next: (response: HttpResponse<Blob>) => {
            if (response.body) {
              const fileName =
                response.headers
                  .get('Content-Disposition')
                  ?.split('filename=')[1]
                  ?.trim() ?? 'CDH-spreadsheet.xlsx';
              const url = window.URL.createObjectURL(response.body);
              const link = document.createElement('a');
              link.href = url;
              link.download = fileName;
              link.click();
              link.remove();
              window.URL.revokeObjectURL(url);
              this.downloadCenterService.downloadState.update((state) => ({
                ...state,
                downloading: false,
                isCompleted: true,
              }));
            }
          },
        });
    }
  }
}
